import { LabelledCheckbox, Layout, Title } from "@introist/react-foundation/v2";
import { useState, useEffect } from "react";

export type MicrosoftCapability = "outlook" | "teams" | "sharepoint";

const capabilityLabels: Record<MicrosoftCapability, { label: string; description: string }> = {
  outlook: {
    label: "Outlook (Email & Calendar)",
    description: "Send emails and schedule calendar events"
  },
  teams: {
    label: "Teams (Chat & Channels)",
    description: "Send channel and chat messages"
  },
  sharepoint: {
    label: "SharePoint (File Access)",
    description: "Access files and data from SharePoint"
  }
};

interface MicrosoftCapabilitySelectorProps {
  selectedCapabilities: MicrosoftCapability[];
  onCapabilitiesChange: (capabilities: MicrosoftCapability[]) => void;
}

export const MicrosoftCapabilitySelector = ({
  selectedCapabilities,
  onCapabilitiesChange
}: MicrosoftCapabilitySelectorProps) => {
  const [capabilities, setCapabilities] = useState<Set<MicrosoftCapability>>(
    new Set(selectedCapabilities)
  );

  useEffect(() => {
    onCapabilitiesChange(Array.from(capabilities));
  }, [capabilities, onCapabilitiesChange]);

  const toggleCapability = (capability: MicrosoftCapability, checked: boolean) => {
    const newCapabilities = new Set(capabilities);
    if (checked) {
      newCapabilities.add(capability);
    } else {
      newCapabilities.delete(capability);
    }
    setCapabilities(newCapabilities);
  };

  return (
    <Layout.Group vertical gap="medium">
      <Title variant="bold">Select capabilities to connect</Title>
      <Layout.Group vertical gap="small">
        {(Object.keys(capabilityLabels) as MicrosoftCapability[]).map(capability => (
          <LabelledCheckbox
            key={capability}
            label={capabilityLabels[capability].label}
            checked={capabilities.has(capability)}
            onChange={(checked) => toggleCapability(capability, checked)}
          />
        ))}
      </Layout.Group>
    </Layout.Group>
  );
};
